import { useCallback, useContext, useMemo } from 'react';
import type { WorkspaceFileUpload } from '@trinity/chat';
import { FileUploadApiCallsContext, FileUploadEffectsContext } from '@trinity/chat-components/containers/WorkspaceFileUpload/Context.js';
import type { Effects } from '@trinity/chat-components/containers/WorkspaceFileUpload/interfaces.effects.js';

type Props = {
  children: React.ReactNode;
  onAbort?: (data: WorkspaceFileUpload) => void;
  onComplete?: (data: WorkspaceFileUpload) => void;
  onFailure?: (data: WorkspaceFileUpload) => void;
  onStart?: (data: WorkspaceFileUpload) => void;
  onQueue?: (data: WorkspaceFileUpload) => void;
};

export const BaseFileUploadEffectsContainer = ({
  onAbort: onAbortProp,
  onComplete: onCompleteProp,
  onFailure: onFailureProp,
  onStart: onStartProp,
  onQueue: onQueueProp,
  ...props
}: Props) => {

  const {
    abort: onAbortApi,
    complete: onCompleteApi,
    failure: onFailureApi,
    start: onStartApi,
    queue: onQueueApi,
  } = useContext(FileUploadApiCallsContext);

  const complete: Effects.OnCompleteUpload.Fn = useCallback(data => {
    return onCompleteApi(data)
      .then(({ fileUpload }) => {
        onCompleteProp?.(fileUpload);
        return { fileUpload };
      });
  }, [
    onCompleteApi,
    onCompleteProp,
  ]);

  const abort: Effects.OnAbortUpload.Fn = useCallback(data => {
    return onAbortApi(data)
      .then(({ fileUpload }) => {
        onAbortProp?.(fileUpload);
        return { fileUpload };
      });
  }, [
    onAbortApi,
    onAbortProp,
  ]);

  const start: Effects.OnStartUpload.Fn = useCallback(data => {
    return onStartApi(data)
      .then(res => {
        onStartProp?.(res.fileUpload);
        return res;
      });
  }, [
    onStartApi,
    onStartProp,
  ]);

  const failure: Effects.OnFailUpload.Fn = useCallback(data => {
    return onFailureApi(data)
      .then(({ fileUpload }) => {
        onFailureProp?.(fileUpload);
        return { fileUpload };
      });
  }, [
    onFailureApi,
    onFailureProp,
  ]);

  const queue: Effects.OnQueueUpload.Fn = useCallback(data => {
    return onQueueApi({
      file: data.file,
      options: data.options,
      parentObjectId: data.parentObjectId,
      uploadIdentifier: data.uploadIdentifier,
      workspaceId: data.workspaceId,
      respondentCountry: data.respondentCountry,
    }).then(res => {
      onQueueProp?.(res.fileUpload);
      return res;
    });
  }, [
    onQueueApi,
    onQueueProp,
  ]);

  const value: Effects.FileUploadEffects = useMemo(() => ({
    abort,
    complete,
    failure,
    queue,
    start,
  }), [
    abort,
    complete,
    failure,
    queue,
    start,
  ]);

  return (
    <FileUploadEffectsContext.Provider value={value}>
      {props.children}
    </FileUploadEffectsContext.Provider>
  );
};

export default BaseFileUploadEffectsContainer;